<template>
    <VField
        v-slot="{ field, errors, meta}"
        v-model="model"
        :name="name || label"
        :label="label || name"
        :rules="rules"
        @input="$emit('update:modelValue', $event.target.value)">
        <div :class="fieldWrapperClasses">
            <div
                v-if="(label || $slots.inputHelper) && $attrs.type !== 'hidden'"
                :class="labelWrapperClasses">
                <label
                    v-if="label && $attrs.type !== 'hidden'"
                    :for="'input_'+name"
                    :title="labelTitle"
                    :class="labelClasses">{{ label }} {{ required ? '*' : '' }}</label>
                <slot name="inputHelper" />
            </div>
            <slot
                :field="field"
                :meta="meta">
                <div :class="inputWrapperClasses">
                    <input
                        v-if="mask"
                        ref="inputRef"
                        v-maska="mask"
                        :id="'input_' + name"
                        v-bind="{...field, ...$attrs}"
                        :class="[props.inputClasses, !meta?.valid && meta?.touched ? props.inputInvalidClasses : '']"
                        :placeholder="$attrs.placeholder || maskPlaceholder">
                    <input
                        v-else
                        ref="inputRef"
                        :id="'input_' + name"
                        v-bind="{...field, ...$attrs}"
                        :class="[props.inputClasses, !meta?.valid && meta?.touched ? props.inputInvalidClasses : '']"
                        :placeholder="$attrs.placeholder">
                    <slot name="inputIcon" />
                </div>
            </slot>

            <ErrorMessage
                :errors="errors"
                :name="name || label"
                :class="'block ' + errorClasses" />
        </div>
    </VField>
</template>

<script setup>
const props = defineProps({
    modelValue: {
        type: [String, Number, Date, Boolean, Object, Array, null],
        default: ''
    },
    name: {
        type: String,
        default: '',
        required: true
    },
    label: {
        type: String,
        default: ''
    },
    fieldWrapperClasses: {
        type: String,
        default: ''
    },
    labelWrapperClasses: {
        type: String,
        default: ''
    },
    labelClasses: {
        type: String,
        default: 'form-label'
    },
    inputWrapperClasses: {
        type: String,
        default: ''
    },
    inputClasses: {
        type: String,
        default: 'form-control'
    },
    inputInvalidClasses: {
        type: String,
        default: 'is-invalid'
    },
    errorClasses: {
        type: String,
        default: 'invalid-feedback'
    },
    rules: {
        type: [String, Array, Object],
        description: 'Vee validate validation rules',
        default: ''
    },
    required: {
        type: Boolean,
        default: false
    },
    labelTitle: {
        type: String,
        default: ''
    },
    mask: {
        type: [String, Object],
        default: null,
        description: 'The mask settings for the input, see https://beholdr.github.io/maska/v3/#/vue'
    }
})

const emits = defineEmits(['update:modelValue'])

const model = computed({
    get: () => props.modelValue,
    set: (newValue) => {
        emits('update:modelValue', newValue)
    }
})

const maskPlaceholder = computed(() => {
    const maskString = props.mask?.mask || props.mask || null
    // tokens #, @ and * are reserved for maska, so we replace them with -, unless they are escaped with !
    return maskString?.replace(/(?<!\\)([#@*])/g, '-')
})

const inputRef = ref(null)

defineExpose({
    input: inputRef
})
</script>